import { type EnergyBudget, type Issue, type System } from "../types";
import {
  computeEnergySnapshot,
  runEnergyDiagnostics,
  type BatterySoc,
  type ComponentStates,
  type LoadStates,
} from "./energy";
import type { DefLookup } from "../catalog";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "../settings/auto-size-vdrop-default";
import { measure } from "../perf-marks";
import { detectBatteryShortCircuits } from "./battery-short";
import { runAiReviewDiagnostics } from "./ai-review";
import { detectAcNeutralProtection } from "./ac-protection";
import { detectAcCircuits } from "./ac-circuits";
import { runDcBreakerDiagnostics } from "./dc-breakers";
import { runControlCircuitDiagnostics } from "./control-circuits";
import { runComponentBehaviorDiagnostics } from "./component-behavior";
import { runPvCombinerDiagnostics } from "./pv-combiners";
import { runBmsDiagnostics } from "./bms";
import { runIsolationDiagnostics } from "./isolation";
import { runDefinitionDiagnostics } from "./definitions";
import { computeEnergizedTerminals } from "./graph";
import { runSenseDiagnostics } from "./sense";
import { runSizingDiagnostics } from "./sizing";
import { detectCableDanglingTerminal, runTerminalDiagnostics } from "./terminals";
import { withoutDanglingCables } from "./cable-endpoints";
import { runTopologyDiagnostics } from "./topology";
import { runVoltageDiagnostics } from "./voltage";
import { withIssueDetails } from "./details";
import { runDesignProfileDiagnostics } from "./design-profile";
import { runFuseBoardDiagnostics } from "./fuse-boards";

export type { EnergyBudget } from "../types";
export { computeEnergyBudget } from "./energy";
export type { BatterySoc, ComponentStates, LoadStates } from "./energy";

const SEVERITY_ORDER: Record<Issue["severity"], number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

type DiagResult = { issues: Issue[]; budget: EnergyBudget; monitorSupplyCableCurrents: ReadonlyMap<string, number> };

/** Keyed on the state objects by reference. The store hands out fresh refs on
 *  every mutation, so stale entries GC naturally. */
const diagCache = new WeakMap<
  System,
  WeakMap<DefLookup, Map<
    ComponentStates,
    Map<BatterySoc, Map<LoadStates, Map<number, DiagResult>>>
  >>
>();

// Module-level, never inline `{}` defaults: the cache keys on object identity,
// so a fresh literal per call would miss every time and re-run the whole pass.
const EMPTY_COMPONENT_STATES: ComponentStates = {};
const EMPTY_BATTERY_SOC: BatterySoc = {};
const EMPTY_LOAD_STATES: LoadStates = {};

export function runAllDiagnostics(
  system: System,
  defLookup: DefLookup,
  componentStates: ComponentStates = EMPTY_COMPONENT_STATES,
  batterySoc: BatterySoc = EMPTY_BATTERY_SOC,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
  loadStates: LoadStates = EMPTY_LOAD_STATES,
): DiagResult {
  let byDefinition = diagCache.get(system);
  if (!byDefinition) {
    byDefinition = new WeakMap();
    diagCache.set(system, byDefinition);
  }
  let l1 = byDefinition.get(defLookup);
  if (!l1) {
    l1 = new Map();
    byDefinition.set(defLookup, l1);
  }
  let l2 = l1.get(componentStates);
  if (!l2) {
    l2 = new Map();
    l1.set(componentStates, l2);
  }
  let l3 = l2.get(batterySoc);
  if (!l3) {
    l3 = new Map();
    l2.set(batterySoc, l3);
  }
  let l4 = l3.get(loadStates);
  if (!l4) {
    l4 = new Map();
    l3.set(loadStates, l4);
  }
  const cached = l4.get(autoSizeTargetPct);
  if (cached) return cached;
  const result = measure("runAllDiagnostics", () =>
    runAllDiagnosticsImpl(
      system,
      defLookup,
      componentStates,
      batterySoc,
      autoSizeTargetPct,
      loadStates,
    ),
  );
  l4.set(autoSizeTargetPct, result);
  return result;
}

function runAllDiagnosticsImpl(
  input: System,
  defLookup: DefLookup,
  componentStates: ComponentStates,
  batterySoc: BatterySoc,
  autoSizeTargetPct: number,
  loadStates: LoadStates,
): DiagResult {
  const system = withoutDanglingCables(input, defLookup);
  // A load behind an open switch is absent from this set, so the budget can
  // drop it without re-walking.
  const energizedTerminals = computeEnergizedTerminals(system, defLookup, {
    componentStates,
    loadStates,
  });
  const { budget, flow } = computeEnergySnapshot(
    system,
    defLookup,
    componentStates,
    batterySoc,
    loadStates,
    energizedTerminals,
  );
  const issues = [
    // Keep the saved evidence visible while calculating only real connections.
    ...(system === input ? [] : detectCableDanglingTerminal(input, defLookup)),
    ...runDesignProfileDiagnostics(system, defLookup, autoSizeTargetPct),
    ...runFuseBoardDiagnostics(system, defLookup, autoSizeTargetPct),
    ...detectBatteryShortCircuits(system, defLookup),
    ...runBmsDiagnostics(system, defLookup),
    ...detectAcNeutralProtection(system, defLookup),
    ...detectAcCircuits(system, defLookup),
    ...runDcBreakerDiagnostics(system, defLookup, autoSizeTargetPct),
    ...runControlCircuitDiagnostics(system, defLookup),
    ...runComponentBehaviorDiagnostics(system, defLookup),
    ...runPvCombinerDiagnostics(system, defLookup),
    ...runIsolationDiagnostics(system, defLookup),
    ...runTopologyDiagnostics(system, defLookup, autoSizeTargetPct),
    ...runSizingDiagnostics(system, defLookup, autoSizeTargetPct),
    ...runTerminalDiagnostics(system, defLookup, autoSizeTargetPct),
    ...runSenseDiagnostics(system, defLookup),
    ...runEnergyDiagnostics(system, budget, defLookup),
    ...runVoltageDiagnostics(system, defLookup),
    ...runDefinitionDiagnostics(system, defLookup),
    ...runAiReviewDiagnostics(system, defLookup, autoSizeTargetPct),
  ];
  issues.sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  );
  return { issues: issues.map(withIssueDetails), budget, monitorSupplyCableCurrents: flow.monitorSupplyCableCurrents };
}
